import { Icon, type IconName } from '@/lib/icons';
import { cn } from '@/lib/cn';
import type { ErkennungStatus } from './use-spracherkennung';

/**
 * Das Bedienelement der Sprachbedienung — überall gleich, damit „hört zu“
 * überall gleich aussieht.
 *
 * ZUGÄNGLICHKEIT: Der Zustand steckt in der Icon-FORM und im Text, nicht in
 * der Farbe. Wer Rot und Grün nicht unterscheidet, sieht trotzdem am Symbol
 * (Mikrofon / Stopp-Quadrat / Spinner / durchgestrichenes Mikrofon), was los
 * ist; Screenreader lesen `aria-label` und die Statusmeldung.
 */

interface StatusDarstellung {
  icon: IconName;
  /** Kurztext neben dem Symbol. */
  text: string;
  /** Vollsatz für Tooltip und Screenreader. */
  erklaerung: string;
}

const STATUS_DARSTELLUNG: Record<ErkennungStatus, StatusDarstellung> = {
  'nicht-unterstuetzt': {
    icon: 'MicOff',
    text: 'Kein Diktat',
    erklaerung: 'Spracherkennung in diesem Browser nicht verfügbar (Chrome oder Edge nötig)',
  },
  inaktiv: { icon: 'Mic', text: 'Diktieren', erklaerung: 'Diktat starten' },
  'hoert-zu': { icon: 'Square', text: 'Hört zu', erklaerung: 'Hört zu — zum Beenden auswählen' },
  verarbeitet: { icon: 'Loader2', text: 'Moment', erklaerung: 'Aufnahme wird ausgewertet' },
  fehler: {
    icon: 'MicOff',
    text: 'Fehler',
    erklaerung: 'Diktat fehlgeschlagen — erneut versuchen',
  },
};

export interface MikrofonKnopfProps {
  status: ErkennungStatus;
  onKlick: () => void;
  /** Text neben dem Symbol anzeigen (z. B. über einem Textbereich). */
  mitBeschriftung?: boolean;
  /** Ergänzt Tooltip und Vorlesetext, z. B. der Feldname. */
  bezeichnung?: string;
  /** Kompakte Variante für die Zeile eines Eingabefelds. */
  kompakt?: boolean;
  deaktiviert?: boolean;
  className?: string;
}

export function MikrofonKnopf({
  status,
  onKlick,
  mitBeschriftung = false,
  bezeichnung,
  kompakt = false,
  deaktiviert = false,
  className,
}: MikrofonKnopfProps) {
  const darstellung = STATUS_DARSTELLUNG[status];
  const nichtVerfuegbar = status === 'nicht-unterstuetzt';
  const hoertZu = status === 'hoert-zu';
  const titel = bezeichnung ? `${darstellung.erklaerung} — ${bezeichnung}` : darstellung.erklaerung;

  return (
    <>
      <button
        type="button"
        onClick={onKlick}
        disabled={deaktiviert}
        aria-disabled={nichtVerfuegbar}
        aria-pressed={hoertZu}
        aria-label={titel}
        title={titel}
        className={cn(
          'inline-flex items-center justify-center gap-1.5 rounded border transition-colors',
          kompakt ? 'h-8 min-w-8 px-1.5 text-xs' : 'h-9 min-w-9 px-2 text-sm',
          hoertZu
            ? 'border-accent-500 bg-accent-100 text-accent-700'
            : 'border-ink-200 bg-paper text-ink-600 hover:border-ink-300 hover:text-ink-800',
          (nichtVerfuegbar || deaktiviert) && 'cursor-not-allowed opacity-60',
          className,
        )}
      >
        <Icon
          name={darstellung.icon}
          size={kompakt ? 14 : 16}
          className={cn(status === 'verarbeitet' && 'animate-spin', hoertZu && 'animate-pulse')}
        />
        {mitBeschriftung ? <span>{darstellung.text}</span> : null}
      </button>
      {/* Zustandswechsel auch ohne Blick auf den Knopf ansagen — die
          Live-Region steht bewusst neben dem Knopf, nicht darin. */}
      <span className="sr-only" role="status">
        {titel}
      </span>
    </>
  );
}
